<template>
  <div>
    <tool-bar :title="toolbarTitle"></tool-bar>
    <loading :loading="loading"></loading>

    <confirmation-dialog
      :show="showDeclineToRejectLoanDialog"
      confirmation-message="Are you sure to continue? Your loan might be rejected..."
      @onConfirm="onConfirmDeclineToRejectLoan"
      @onNotConfirm="showDeclineToRejectLoanDialog=false">
    </confirmation-dialog>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false">
    </error-alert-item>

    <activity-redirect-item
      :loan-tasks="tasks"
      :current-loan-task="currentTaskName"
      :is-loan-task="true"
      :redirect-activity="redirectActivity">
    </activity-redirect-item>

    <stage-comment-item></stage-comment-item>

    <div class="margin_top_40 margin-left-10 margin-right-10" v-if="showUI">

      <div class="item-heading-desc-div">
        <weex-text class="item-sm-heading">{{heading}}</weex-text>
      </div>


      <div class="item-row-alignment margin_top_20">
        <weex-text class="font-size-base">Loan Applied Amount&nbsp;:&nbsp;</weex-text>
        <weex-text class="font-size-base">{{ loanAppliedAmountText }}</weex-text>
      </div>

      <div class="item-row-alignment margin-top-10">
        <weex-text class="font-size-base">Loan Approved Amount&nbsp;:&nbsp;</weex-text>
        <weex-text class="font-size-base">{{ loanSanctionAmountText }}</weex-text>
      </div>

      <div class="margin-top-10">
        <div v-if="updateBankStatements" class="margin_top_20">
          <weex-text class="font-size-sm-base">{{ reUploadBankStatementText }}</weex-text>
          <wxc-button
            text="Re-Upload Bank Statement"
            class="button_horizontal_center margin-top-10"
            type="blue"
            @wxcButtonClicked="onBankStatementUploadClicked">
          </wxc-button>

          <weex-text class="margin_top_20 item-sm-description text-color-gray">-OR-</weex-text>
        </div>

        <div class="margin_top_20">
          <weex-text class="font-size-base">Change Loan Amount (Optional)</weex-text>
          <weex-text class="text-color-gray font-size-sm-base">{{ newLoanAmountLabel }}</weex-text>
          <input
            class="input_size-sm-base_with_border_bottom"
            type="number"
            :value="newLoanAmount"
            @input="newLoanAmount=$event.target.value">

          <wxc-button
            text="Change Loan Amount"
            class="button_horizontal_center margin_top_20"
            type="blue"
            @wxcButtonClicked="onChangeLoanAmountClicked">
          </wxc-button>
        </div>

        <weex-text class="margin_top_20 item-sm-description text-color-gray">-OR-</weex-text>

        <wxc-button
          text="Decline to Reject Loan"
          class="button_horizontal_center margin_top_20 margin-bottom-20"
          type="red"
          @wxcButtonClicked="onDeclineToRejectLoanClicked">
        </wxc-button>
      </div>
    </div>
  </div>
</template>

<script>
import ToolBar from "../layout/ToolBar";
import WxcButton from "@/../weex-ui/packages/wxc-button/index"
import loanScopeDataHolder from "@/data/model/LoanScopeDataHolder";
import ActivityRedirectItem from "../layout/ActivityRedirectItem";
import ErrorAlertItem from "@/components/layout/ErrorAlertItem";
import { Activities } from "@/utils/ActivityNavigator";
import Loading from "@/components/layout/Loading";
import DataSource from "@/data/DataSource";
import EzUtils from "@/utils/EzUtils";
import { Constants } from "@/components/Constants";
import AlertService from "@/components/core/alert/AlertService";
import { LoanApprovedAmountMessage } from "@/utils/MessageConstantUtil";
import loanTasksActivityNavigator from "@/utils/LoanTasksActivityNavigator";
import StageCommentItem from "../layout/StageCommentItem";
import LoanTasksUtility from "@/utils/LoanTasksUtility"
import AttributeUtil from "../../utils/AttributeUtil";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import storageUtil from "../../utils/StorageUtil";

const INR_SIGN = "\u20B9"
const LOAN_APPROVED_AMOUNT_UPDATE_LOAN_AMOUNT = "update_loan_amount"
const LOAN_APPROVED_AMOUNT_UPDATE_BANK_STATEMENT = "update_bank_statement"
const LOAN_APPROVED_AMOUNT_REJECT = "reject"
const SANCTIONED_AMOUNT_STATUS_NAME = "Sanctioned Amount Status"
const SANCTIONED_AMOUNT_STATUS_KEY = "sanctioned.amount.status"
const UPDATED_LOAN_AMOUNT_NAME = "Updated Loan Amount"
const UPDATED_LOAN_AMOUNT_KEY = "updated.loan.amount"

export default {
  name: "LoanApprovedAmount",
  components: {
    ActivityRedirectItem,
    ToolBar,
    WxcButton,
    ErrorAlertItem,
    Loading,
    ConfirmationDialog,
    StageCommentItem
  },

  data: () => ({
    toolbarTitle: "Loan Amount Details",
    loading: false,
    errorAlertEvent: false,
    networkError: {},
    showUI: false,
    showDeclineToRejectLoanDialog: false,
    newLoanAmount: "",
    sanctionedAmount: "",
    appliedAmount: "",
    allowedMinimumAmount: "",
    updateBankStatements: false
  }),

  methods: {
    showError(error) {
      this.loading = false
      this.networkError = error
      this.errorAlertEvent = true
    },

    showToast(message) {
      AlertService.showToast(message)
    },

    onBankStatementUploadClicked() {
      const attributeDto = AttributeUtil.getAttributeUpdateDto(
        Constants.LOANS,
        loanScopeDataHolder.getLoanId(),
        AttributeUtil.getSingleAttributeData(
          SANCTIONED_AMOUNT_STATUS_KEY,
          SANCTIONED_AMOUNT_STATUS_NAME,
          LOAN_APPROVED_AMOUNT_UPDATE_BANK_STATEMENT
        )
      );

      this.saveUserSelectedAttribute(attributeDto);
    },

    onDeclineToRejectLoanClicked() {
      this.showDeclineToRejectLoanDialog = true
    },

    onConfirmDeclineToRejectLoan() {
      this.showDeclineToRejectLoanDialog = false
      const attributeDto = AttributeUtil.getAttributeUpdateDto(
        Constants.LOANS,
        loanScopeDataHolder.getLoanId(),
        AttributeUtil.getSingleAttributeData(
          SANCTIONED_AMOUNT_STATUS_KEY,
          SANCTIONED_AMOUNT_STATUS_NAME,
          LOAN_APPROVED_AMOUNT_REJECT
        )
      );

      this.saveUserSelectedAttribute(attributeDto);
    },

    onChangeLoanAmountClicked() {
      if (this.checkForChangeLoanAmountValidation()) {
        const attributeDto = AttributeUtil.getAttributeUpdateDto(
          Constants.LOANS,
          loanScopeDataHolder.getLoanId(),
          AttributeUtil.getSingleAttributeData(
            SANCTIONED_AMOUNT_STATUS_KEY,
            SANCTIONED_AMOUNT_STATUS_NAME,
            LOAN_APPROVED_AMOUNT_UPDATE_LOAN_AMOUNT
          ),
          AttributeUtil.getSingleAttributeData(
            UPDATED_LOAN_AMOUNT_KEY,
            UPDATED_LOAN_AMOUNT_NAME,
            this.newLoanAmount
          )
        );

        this.saveUserSelectedAttribute(attributeDto);
      }
    },

    checkForChangeLoanAmountValidation() {
      if (EzUtils.isEmpty(this.newLoanAmount)) {
        this.showToast(LoanApprovedAmountMessage.ENTER_AMOUNT)
        return false;
      }

      if (!EzUtils.containsOnlyNumbers(this.newLoanAmount)) {
        this.showToast(LoanApprovedAmountMessage.ENTER_VALID_AMOUNT)
        return false;
      }

      if (this.newLoanAmount > this.sanctionedAmount) {
        this.showToast(LoanApprovedAmountMessage.AMOUNT_CANNOT_MORE_THAN_APPROVED)
        return false
      }

      if (this.newLoanAmount === 0
        || (this.allowedMinimumAmount && this.newLoanAmount < this.allowedMinimumAmount)
      ) {
        this.showToast(LoanApprovedAmountMessage.AMOUNT_CANNOT_LESS_THAN_MINIMUM)
        return false;
      }

      return true;
    },

    saveUserSelectedAttribute(attributeDto) {
      this.loading = true

      DataSource.createOrUpdateAttribute(attributeDto)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data)
          } else {
            this.getNextTask();
          }
        })
    },

    getNextTask() {
      this.loading = true;
      loanTasksActivityNavigator.getNextTask(this.tasks)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data)
          }
        });
    },

    fetchStageConfig() {
      this.loading = true
      LoanTasksUtility.getLoanStageConfigData(
        loanScopeDataHolder.getLoanId(),
        Activities.LOAN_APPROVED_AMOUNT
      )
        .then(stageConfigDataRes => {
          this.loading = false;
          if (stageConfigDataRes.errorOccurred) {
            this.showError(stageConfigDataRes.data)
          } else {
            this.showUI = true
            const loanAmountDetails = stageConfigDataRes.data
            this.sanctionedAmount = loanAmountDetails.sanctionedAmount
            this.appliedAmount = loanAmountDetails.appliedAmount
            this.allowedMinimumAmount = loanAmountDetails.allowedMinimumAmount
            this.updateBankStatements = loanAmountDetails.updateBankStatements
            this.newLoanAmount = loanAmountDetails.clientUpdatedAmount
              ? parseInt(loanAmountDetails.clientUpdatedAmount)
              : ""
          }
        })
    }
  },

  created() {
    this.fetchStageConfig()
  },

  computed: {
    redirectActivity() {
      return Activities.LOAN_DETAILS
    },

    tasks() {
      return [Activities.LOAN_APPROVED_AMOUNT]
    },

    currentTaskName() {
      return loanScopeDataHolder.getCurrentLoanTask()
        ? loanScopeDataHolder.getCurrentLoanTask().name : ''
    },

    heading() {
      return "Please find below approved/sanctioned loan amount";
    },

    reUploadBankStatementText() {
      return "Re-upload another Bank Statement. (To try for higher Loan Approved Amount)"
    },

    newLoanAmountLabel() {
      return "Amount (less than or equal to the Loan Approved Amount)"
    },

    loanAppliedAmountText() {
      return this.appliedAmount ? `${INR_SIGN}${parseInt(this.appliedAmount)}` : "";
    },

    loanSanctionAmountText() {
      return this.sanctionedAmount ? `${INR_SIGN}${parseInt(this.sanctionedAmount)}` : "";
    },
  }
};
</script>

<style scoped src="../../assets/style/base.css"></style>


